/**
 * @fileoverview One value in the conflict-check brief card (MOR-993)
 *
 * Purpose: the brief stores every field as a plain string, but a few shapes
 * deserve better than a wall of text — a fifty-name trading-name list becomes
 * a capped run of chips with a "+N more" toggle, a "9 — see the attached
 * party schedule" pointer gets its count set apart from its note, and an
 * "N/A" (the searches found nothing) recedes so real findings stand out.
 *
 * Used by: conflict-brief-view.tsx
 */

import { useState } from "react";

import { briefValueItems } from "@cvx/preAppointment/conflictBriefFormat";
import { cn } from "@/utils/misc";

/** Chips shown before the list collapses behind "+N more". */
const LIST_PREVIEW = 6;

const NOT_AVAILABLE = "N/A";

/** "9 — see the attached party schedule" → count + note. */
const SCHEDULED_VALUE = /^(\d+)\s+—\s+(.+)$/;

interface ConflictBriefFieldValueProps {
  value: string;
}

/**
 * @component
 * @example
 * <ConflictBriefFieldValue value="Bunnings, Bunnings Trade, Hardware Cafe, …" />
 */
export function ConflictBriefFieldValue({
  value,
}: ConflictBriefFieldValueProps) {
  const [expanded, setExpanded] = useState(false);

  if (value === NOT_AVAILABLE) {
    return <span className="text-muted-foreground/60">{NOT_AVAILABLE}</span>;
  }

  const scheduled = SCHEDULED_VALUE.exec(value);
  if (scheduled) {
    return (
      <span>
        <span className="font-medium">{scheduled[1]}</span>
        <span className="text-muted-foreground"> · {scheduled[2]}</span>
      </span>
    );
  }

  const items = briefValueItems(value);
  if (!items) {
    return <span className="break-words">{value}</span>;
  }

  const visible = expanded ? items : items.slice(0, LIST_PREVIEW);
  const hidden = items.length - visible.length;

  return (
    <span className="flex flex-wrap items-center gap-1">
      {visible.map((item, index) => (
        <span
          key={`${item}-${index}`}
          className={cn(
            "inline-flex max-w-full items-center rounded border bg-muted/50 px-1.5 py-px text-xs leading-4",
          )}
        >
          <span className="truncate" title={item}>
            {item}
          </span>
        </span>
      ))}
      {(hidden > 0 || expanded) && (
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show fewer" : `+${hidden} more`}
        </button>
      )}
    </span>
  );
}
